import { useCallback, useEffect, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { logRBIEvent } from 'features/analytics';
import { AuthenticationPhases, CustomEventNames, EventTypes } from 'features/analytics/constants';

import { toaster } from 'components/toaster/toaster';
import {
  ISignUpCompleteQuery,
  SignUpCompleteDocument,
  useSignInJwtMutation,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { useConfirmJwt } from 'state/auth/hooks/use-confirm-jwt';
import { t } from 'state/intl/translations';
import { platform, welcomeEmailDomain } from 'utils/environment';
import logger from 'utils/logger';

import { autoSignInEvent } from '../utils/analytics';

const POLLING_INTERVAL_MS = 2000;
const COMPLETION_TIMEOUT_MS = 30_000;

interface SignupCompletionInput {
  jwt: string;
  email: string;
}

interface SignupAttempt extends SignupCompletionInput {
  controller: AbortController;
  phase: 'polling' | 'signing-in';
  pollTimeout?: ReturnType<typeof setTimeout>;
  completionTimeout?: ReturnType<typeof setTimeout>;
}

type FailureReason = 'timeout' | 'query-error' | 'sign-in-error';

const failureMessages: Record<FailureReason, string> = {
  timeout: 'Signup completion timed out',
  'query-error': 'Signup completion query failed',
  'sign-in-error': 'Signup automatic sign-in failed',
};

/** Owns the post-creation wait and automatic sign-in for both signup variants. */
export const useSignupCompletion = ({ onError }: { onError(email: string): void }) => {
  const client = useApolloClient();
  const [signInJwtMutation] = useSignInJwtMutation();
  const { handleConfirmJwt } = useConfirmJwt();
  const { isAuthenticated, onSignInSuccess } = useAuthContext();
  const [status, setStatus] = useState<'idle' | 'loading' | 'failed' | 'complete'>('idle');
  const activeAttempt = useRef<SignupAttempt | null>(null);
  const mounted = useRef(true);
  const callbacks = useRef({ onError, onSignInSuccess, handleConfirmJwt, signInJwtMutation });
  callbacks.current = { onError, onSignInSuccess, handleConfirmJwt, signInJwtMutation };
  const authenticated = useRef(isAuthenticated);
  authenticated.current = isAuthenticated;

  const cancel = useCallback(() => {
    const attempt = activeAttempt.current;
    activeAttempt.current = null;
    if (attempt) {
      clearTimeout(attempt.pollTimeout);
      clearTimeout(attempt.completionTimeout);
      attempt.controller.abort();
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancel();
    };
  }, [cancel]);

  useEffect(() => {
    // Another authentication flow may finish while this account is still being provisioned.
    if (isAuthenticated && activeAttempt.current?.phase === 'polling') {
      cancel();
      setStatus('complete');
    }
  }, [cancel, isAuthenticated]);

  const fail = useCallback(
    (attempt: SignupAttempt, reason: FailureReason) => {
      if (activeAttempt.current !== attempt) {
        return;
      }
      cancel();
      setStatus('failed');
      autoSignInEvent({ phase: AuthenticationPhases.ERROR, message: failureMessages[reason] });
      // Keep credentials, email, and backend error payloads out of this diagnostic.
      logger.warn({ message: 'Signup completion failed', signupCompletion: { reason } });
      callbacks.current.onError(attempt.email);
    },
    [cancel]
  );

  const startCompletion = useCallback(
    ({ jwt, email }: SignupCompletionInput) => {
      if (!mounted.current || !jwt || authenticated.current || activeAttempt.current?.jwt === jwt) {
        return;
      }

      cancel();
      const attempt: SignupAttempt = {
        jwt,
        email,
        phase: 'polling',
        controller: new AbortController(),
      };
      activeAttempt.current = attempt;
      setStatus('loading');
      autoSignInEvent({ phase: AuthenticationPhases.START });

      // Start after account creation, regardless of whether signup is in a drawer or a route.
      attempt.completionTimeout = setTimeout(() => fail(attempt, 'timeout'), COMPLETION_TIMEOUT_MS);

      const poll = async () => {
        try {
          const { data } = await client.query<ISignUpCompleteQuery>({
            query: SignUpCompleteDocument,
            variables: { jwt },
            fetchPolicy: 'no-cache',
            errorPolicy: 'none',
            context: {
              queryDeduplication: false,
              fetchOptions: { signal: attempt.controller.signal },
            },
          });

          if (activeAttempt.current !== attempt) {
            return;
          }

          if (!data?.signUpComplete) {
            attempt.pollTimeout = setTimeout(() => void poll(), POLLING_INTERVAL_MS);
            return;
          }

          clearTimeout(attempt.completionTimeout);
          // Mark the handoff before awaiting a mutation so rerenders cannot sign in twice.
          attempt.phase = 'signing-in';
          autoSignInEvent({ phase: AuthenticationPhases.VALIDATION });
          await callbacks.current.signInJwtMutation({
            variables: { input: { email, stage: welcomeEmailDomain(), platform: platform() } },
          });
          if (activeAttempt.current !== attempt) {
            return;
          }

          await callbacks.current.handleConfirmJwt(jwt, email);
          if (activeAttempt.current !== attempt) {
            return;
          }

          cancel();
          setStatus('complete');
          logRBIEvent({
            name: CustomEventNames.AUTO_SIGN_IN,
            type: EventTypes.Other,
            attributes: { signUpType: email ? 'Email' : 'Phone Number' },
          });
          autoSignInEvent({ phase: AuthenticationPhases.COMPLETE });
          toaster.positive(t('autoSignInSuccess'));
          callbacks.current.onSignInSuccess();
        } catch {
          fail(attempt, attempt.phase === 'polling' ? 'query-error' : 'sign-in-error');
        }
      };

      void poll();
    },
    [cancel, client, fail]
  );

  return { startCompletion, isLoading: status === 'loading', hasFailed: status === 'failed' };
};
